import { boundsOfPoints } from './bounds';
import { circlePerpendiculars, circleTangents, projectToRadius, QUADRANT_ANGLES } from './circular';
import type { ShapeOps } from './shape-ops';
import {
  add,
  angleOf,
  dist,
  distSq,
  fromPolar,
  normalizeDegrees,
  reflectAcross,
  rotateAbout,
  sub,
  type Vec2,
} from './vec2';

/** A circular arc swept counter-clockwise from `start` to `end`, in degrees. */
export interface Arc {
  readonly type: 'arc';
  readonly center: Vec2;
  readonly radius: number;
  readonly start: number;
  readonly end: number;
}

export const arc = (center: Vec2, radius: number, start: number, end: number): Arc => ({
  type: 'arc',
  center,
  radius,
  start,
  end,
});

export function arcSweep(a: Arc): number {
  const sweep = normalizeDegrees(a.end - a.start);
  return sweep === 0 ? 360 : sweep;
}

export function arcContainsAngle(a: Arc, degrees: number): boolean {
  return normalizeDegrees(degrees - a.start) <= arcSweep(a) + 1e-9;
}

export function arcPointAt(a: Arc, t: number): Vec2 {
  return fromPolar(a.center, a.radius, a.start + arcSweep(a) * t);
}

const onArc = (a: Arc, p: Vec2): boolean => arcContainsAngle(a, angleOf(sub(p, a.center)));

const arcEndpoints = (a: Arc): Vec2[] => [arcPointAt(a, 0), arcPointAt(a, 1)];

const arcQuadrants = (a: Arc): Vec2[] =>
  QUADRANT_ANGLES.filter((angle) => arcContainsAngle(a, angle)).map((angle) =>
    fromPolar(a.center, a.radius, angle),
  );

export const ARC_OPS: ShapeOps<Arc> = {
  measurements: (a) => {
    const [start, end] = arcEndpoints(a) as [Vec2, Vec2];
    return [
      { label: 'Center', kind: 'point', value: a.center },
      { label: 'Radius', kind: 'length', value: a.radius },
      { label: 'Start angle', kind: 'angle', value: normalizeDegrees(a.start) },
      { label: 'End angle', kind: 'angle', value: normalizeDegrees(a.end) },
      { label: 'Sweep', kind: 'angle', value: arcSweep(a) },
      { label: 'Arc length', kind: 'length', value: (Math.PI * a.radius * arcSweep(a)) / 180 },
      { label: 'Chord', kind: 'length', value: dist(start, end) },
      { label: 'Start', kind: 'point', value: start },
      { label: 'End', kind: 'point', value: end },
      { label: 'Midpoint', kind: 'point', value: arcPointAt(a, 0.5) },
    ];
  },
  endpoints: arcEndpoints,
  midpoint: (a) => arcPointAt(a, 0.5),
  center: (a) => a.center,
  quadrants: arcQuadrants,
  closestPoint: (a, p) => {
    if (onArc(a, p)) return projectToRadius(a.center, a.radius, p);
    const [first, second] = arcEndpoints(a) as [Vec2, Vec2];
    return distSq(first, p) <= distSq(second, p) ? first : second;
  },
  bounds: (a) => boundsOfPoints([...arcEndpoints(a), ...arcQuadrants(a)]),
  carrier: (a) => ({ kind: 'circle', center: a.center, radius: a.radius }),
  liesOn: (a, p, eps) => Math.abs(dist(a.center, p) - a.radius) <= eps && onArc(a, p),
  perpendicularFrom: (a, from) =>
    circlePerpendiculars(a.center, a.radius, from).filter((p) => onArc(a, p)),
  tangentFrom: (a, from) => circleTangents(a.center, a.radius, from).filter((p) => onArc(a, p)),
  offset: (a, distance) =>
    a.radius + distance > 0 ? arc(a.center, a.radius + distance, a.start, a.end) : null,
  offsetDistance: (a, p) => dist(a.center, p) - a.radius,
  translate: (a, delta) => arc(add(a.center, delta), a.radius, a.start, a.end),
  rotate: (a, center, degrees) =>
    arc(
      rotateAbout(a.center, center, degrees),
      a.radius,
      normalizeDegrees(a.start + degrees),
      normalizeDegrees(a.end + degrees),
    ),
  // A reflection turns the sweep clockwise, so the reflected ends swap to stay counter-clockwise.
  mirror: (a, from, to) => {
    const axis = 2 * angleOf(sub(to, from));
    return arc(
      reflectAcross(a.center, from, to),
      a.radius,
      normalizeDegrees(axis - a.end),
      normalizeDegrees(axis - a.start),
    );
  },
  parameterOf: (a, p) => normalizeDegrees(angleOf(sub(p, a.center)) - a.start) / arcSweep(a),
  period: (a) => 360 / arcSweep(a),
  // The ends are kept exactly, so a piece that reaches one leaves that angle field as it was.
  piece: (a, t0, t1) =>
    arc(
      a.center,
      a.radius,
      t0 === 0 ? a.start : normalizeDegrees(a.start + arcSweep(a) * t0),
      t1 === 1 ? a.end : normalizeDegrees(a.start + arcSweep(a) * t1),
    ),
};
